import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { monthLabel } from "@/pages/CompletedProducts";
import DateRangeFilter, { rangeFor, defaultGranularity } from "@/components/DateRangeFilter";
import KpiCard, { fmtNum, fmtPct } from "@/components/KpiCard";
import EffBadge from "@/components/EffBadge";
import { useThresholds, effColor } from "@/context/ThresholdContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, BarChart, Cell,
} from "recharts";

const tooltipStyle = { backgroundColor: "#111827", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 };
const TICK = { fontSize: 10, fill: "#9CA3AF" };
const TICK_DEPT = { fontSize: 11, fill: "#9CA3AF" };
const LEGEND_STYLE = { fontSize: 11 };

export default function Dashboard() {
  const [range, setRange] = useState(() => rangeFor({ key: "7d", days: 7 }));
  const [dept, setDept] = useState("");
  const [summary, setSummary] = useState(null);
  const [byDept, setByDept] = useState([]);
  const [trend, setTrend] = useState([]);
  const [bottleneck, setBottleneck] = useState(null);
  const [completed, setCompleted] = useState(null);
  const [meta, setMeta] = useState({ departments: [] });
  const { thresholds } = useThresholds();

  useEffect(() => { api.get("/meta/options").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const base = `start=${range.start}&end=${range.end}`;
    const dp = dept ? `&department_code=${dept}` : "";
    api.get(`/analytics/summary?${base}${dp}`).then((r) => setSummary(r.data));
    api.get(`/analytics/by?group=department&${base}`).then((r) => setByDept(r.data));
    const g = defaultGranularity(range.start, range.end);
    const trendUrl = dept
      ? `/analytics/trend?${base}&granularity=${g}&group=department&ids=${dept}`
      : `/analytics/trend?${base}&granularity=${g}`;
    api.get(trendUrl).then((r) => {
      const s = r.data.series?.[0];
      setTrend(s ? s.data.map((d) => ({ ...d, name: d.label })) : []);
    });
    api.get(`/analytics/bottleneck?${base}`).then((r) => setBottleneck(r.data));
    const cm = new Date().toISOString().slice(0, 7);
    api.get(`/analytics/monthly-production?start_month=${cm}&end_month=${cm}`)
      .then((r) => setCompleted(r.data.monthly?.[0] || null)).catch(() => {});
  }, [range, dept]);

  const deptName = (code) => meta.departments.find((d) => d.code === code)?.name || code;

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Fabrika Geneli</h1>
          <p className="text-sm text-muted-foreground mt-1">{range.start} — {range.end}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={dept || "ALL"} onValueChange={(v) => setDept(v === "ALL" ? "" : v)}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="dashboard-department-filter">
              <SelectValue placeholder="Tüm Bölümler" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL" data-testid="dashboard-department-all">Tüm Bölümler</SelectItem>
              {meta.departments.map((d) => (
                <SelectItem key={d.code} value={d.code} data-testid={`dashboard-department-${d.code}`}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DateRangeFilter value={range} onChange={setRange} testidPrefix="dashboard-date" />
        </div>
      </div>

      <KpiGrid summary={summary} thresholds={thresholds} />

      {completed && (
        <Card className="p-4 bg-card border-border flex flex-wrap items-center justify-between gap-3" data-testid="dashboard-completed-products">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Tamamlanan Ürünler — {monthLabel(completed.month)}
            </div>
            <div className="mt-1 text-lg font-bold font-mono" data-testid="dashboard-completed-values">
              Makine: <span className="text-blue-400">{fmtNum(completed.makine)}</span>
              {"  ·  "}Kompresör: <span className="text-amber-400">{fmtNum(completed.kompresor)}</span>
              {"  ·  "}Toplam: <span className="text-primary">{fmtNum(completed.total)}</span> adet
            </div>
          </div>
          <Link to="/tamamlanan-urunler" data-testid="dashboard-completed-link">
            <Button variant="outline" size="sm" className="text-xs">Detay</Button>
          </Link>
        </Card>
      )}

      <BottleneckPanel findings={bottleneck?.findings} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-4 bg-card border-border" data-testid="dashboard-production-trend-chart">
          <h3 className="text-lg font-medium mb-3">Üretim Trendi — Plan / Standart / Gerçek</h3>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="name" tick={TICK} />
              <YAxis tick={TICK} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="actual" name="Gerçek" fill="#DC2626" radius={[3, 3, 0, 0]} />
              <Line dataKey="plan" name="Plan" stroke="#2563EB" strokeWidth={2} dot={false} />
              <Line dataKey="standard" name="Standart Kapasite" stroke="#6B7280" strokeWidth={2} strokeDasharray="5 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4 bg-card border-border" data-testid="dashboard-efficiency-trend-chart">
          <h3 className="text-lg font-medium mb-3">Verimlilik Trendi (%)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="name" tick={TICK} />
              <YAxis domain={[0, 120]} tick={TICK} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => `%${v}`} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Line dataKey="efficiency" name="Kapasite Verimliliği" stroke="#DC2626" strokeWidth={2.5} dot={false} />
              <Line dataKey="plan_realization" name="Plan Gerçekleşme" stroke="#2563EB" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-4 bg-card border-border" data-testid="dashboard-department-comparison">
        <h3 className="text-lg font-medium mb-3">Bölüm Karşılaştırması (Verimlilik %)</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={byDept.map((d) => ({ ...d, name: deptName(d.key) }))} layout="vertical" margin={{ left: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" horizontal={false} />
            <XAxis type="number" domain={[0, 120]} tick={TICK} />
            <YAxis type="category" dataKey="name" width={90} tick={TICK_DEPT} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => `%${v}`} />
            <Bar dataKey="efficiency" name="Verimlilik %" radius={[0, 3, 3, 0]}>
              {byDept.map((d) => <Cell key={d.key} fill={effColor(d.efficiency, thresholds)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <DepartmentTable byDept={byDept} deptName={deptName} />
    </div>
  );
}

function KpiGrid({ summary, thresholds }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="dashboard-kpi-grid">
      <KpiCard label="Standart Kapasite" value={fmtNum(summary?.standard)} testid="kpi-standard-capacity" />
      <KpiCard label="Plan" value={fmtNum(summary?.plan)} testid="kpi-plan" />
      <KpiCard label="Gerçek Üretim" value={fmtNum(summary?.actual)} testid="kpi-actual" />
      <KpiCard label="Kapasite Verimliliği" value={fmtPct(summary?.efficiency)}
        color={effColor(summary?.efficiency, thresholds)} testid="kpi-efficiency" />
      <KpiCard label="Plan Gerçekleşme" value={fmtPct(summary?.plan_realization)}
        color={effColor(summary?.plan_realization, thresholds)} testid="kpi-plan-realization" />
      <KpiCard label="OEE" value={fmtPct(summary?.oee)} testid="kpi-oee" />
      <KpiCard label="İşçilik Verimliliği" value={fmtPct(summary?.labor_efficiency)} testid="kpi-labor" />
      <KpiCard label="Duruş (dk)" value={fmtNum(summary?.downtime)} testid="kpi-downtime" />
      <KpiCard label="Fazla Mesai (dk)" value={fmtNum(summary?.overtime)} testid="kpi-overtime" />
      <KpiCard label="Fire / Hatalı" value={fmtNum(summary?.scrap)}
        sub={`Kalite: ${fmtPct(summary?.quality_rate)}`} testid="kpi-scrap" />
    </div>
  );
}

function BottleneckPanel({ findings }) {
  if (!findings?.length) return null;
  return (
    <Card className="p-4 bg-amber-500/5 border-amber-500/30" data-testid="dashboard-bottleneck-panel">
      <div className="flex items-center gap-2 text-amber-400 font-semibold text-sm mb-2">
        <AlertTriangle size={16} /> Darboğaz Analizi
      </div>
      <ul className="space-y-1">
        {findings.slice(0, 4).map((f, i) => (
          <li key={f} className="text-sm text-muted-foreground" data-testid={`dashboard-finding-${i}`}>• {f}</li>
        ))}
      </ul>
    </Card>
  );
}

function DepartmentTable({ byDept, deptName }) {
  return (
    <Card className="p-4 bg-card border-border" data-testid="dashboard-department-table">
      <h3 className="text-lg font-medium mb-3">Bölüm Detayları</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="text-left py-2 pr-4">Bölüm</th>
              <th className="text-right py-2 pr-4">Standart</th>
              <th className="text-right py-2 pr-4">Plan</th>
              <th className="text-right py-2 pr-4">Gerçek</th>
              <th className="text-right py-2 pr-4">Verimlilik</th>
              <th className="text-right py-2 pr-4">Duruş (dk)</th>
              <th className="text-right py-2 pr-4">Fazla Mesai (dk)</th>
              <th className="text-right py-2">Fire</th>
            </tr>
          </thead>
          <tbody>
            {byDept.map((d) => (
              <tr key={d.key} className="border-b border-border/50" data-testid={`dashboard-dept-row-${d.key}`}>
                <td className="py-2.5 pr-4 font-medium">{deptName(d.key)}</td>
                <td className="text-right py-2.5 pr-4 font-mono">{fmtNum(d.standard)}</td>
                <td className="text-right py-2.5 pr-4 font-mono">{fmtNum(d.plan)}</td>
                <td className="text-right py-2.5 pr-4 font-mono">{fmtNum(d.actual)}</td>
                <td className="text-right py-2.5 pr-4"><EffBadge value={d.efficiency} testid={`dashboard-dept-eff-${d.key}`} /></td>
                <td className="text-right py-2.5 pr-4 font-mono text-muted-foreground">{fmtNum(d.downtime)}</td>
                <td className="text-right py-2.5 pr-4 font-mono text-muted-foreground">{fmtNum(d.overtime)}</td>
                <td className="text-right py-2.5 font-mono text-muted-foreground">{fmtNum(d.scrap)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
